/* eslint-disable jsx-a11y/label-has-associated-control */
import { Fragment, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import Select from 'react-select';
import Modal from 'react-responsive-modal';
import * as pdfjsLib from 'pdfjs-dist';
import { Page } from '../../components';
import { ReactComponent as StepComplete } from '../../assets/images/step-complete.svg';
import { ReactComponent as RadioChecked } from '../../assets/images/radio-button-checked.svg';
import { ReactComponent as RadioUnchecked } from '../../assets/images/radio-button-unchecked.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/info-stroke.svg';
import ProteanCollapsible from '../../components/Collapsible/Collapsible';
import useMappingToolStore from './store';
import { fitScaleFor } from './pdfScale';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;

const STEPS = [
  { key: 'upload', label: 'Upload file' },
  { key: 'mapping', label: 'Mapping' },
  { key: 'review', label: 'Review' },
  { key: 'testing', label: 'Testing' }
];

const CURRENT_STEP_INDEX = 1;
const UPLOAD_PATH = '/report_template_mapper/upload';
const REVIEW_PATH = '/report_template_mapper/review';
const NEW_FIELD_SIZE = { w: 90, h: 22 };
const NEW_ANSWER_FIELD_SIZE = { w: 22, h: 22 };
const RESIZE_HOTZONE_PX = 16;
const MIN_FIELD_SIZE = 8;

const SelectStyled = {
  menuPortal: base => ({ ...base, zIndex: 9999 })
};

const ANSWER_FORMAT_OPTIONS = [
  { value: 'single', label: 'Single box — Pass/Fail/N-A share one box' },
  { value: 'double', label: 'Double box — Pass/Fail together, N-A separate' },
  { value: 'triple', label: 'Triple box — Pass, Fail, N-A each their own box' }
];

// Prompts map as answer handles only -- real forms already print the question text statically.
const ANSWER_FORMATS = {
  single: [{ key: 'any', label: 'Ans', cls: 'qs-mixed' }],
  double: [
    { key: 'passfail', label: 'P/F', cls: 'qs-mixed' },
    { key: 'na', label: 'N/A', cls: 'qs-na' }
  ],
  triple: [
    { key: 'fail', label: 'F', cls: 'qs-fail' },
    { key: 'pass', label: 'P', cls: 'qs-pass' },
    { key: 'na', label: 'N/A', cls: 'qs-na' }
  ]
};

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];
// Months only -- 'calendar:year' is offered once, from the store's General info list
// (CURRENT_YEAR_ITEM); listing it here as well put the same key in two panels.
const CALENDAR_ITEMS = MONTH_NAMES.map((name, index) => ({
  key: `calendar:month:${index + 1}`,
  label: name
}));

let fieldIdCounter = 0;
const nextFieldId = () => {
  fieldIdCounter += 1;
  return `field-${fieldIdCounter}`;
};

const KeyChip = ({ label, onDragStart }) => (
  <li className="mapping-tool-key-chip" draggable onDragStart={onDragStart}>
    {label}
  </li>
);

KeyChip.propTypes = {
  label: PropTypes.string.isRequired,
  onDragStart: PropTypes.func.isRequired
};

// One row per prompt: draggable outcome handles plus the question text as context.
const PromptRow = ({ prompt, badgeDefs, usedCounts, onDragStart }) => (
  <li className="mapping-tool-prompt-row">
    <span className="mapping-tool-qs-badges">
      {badgeDefs.map(def => {
        const count = usedCounts[def.key] ?? 0;
        const title =
          count > 0
            ? `${def.label}: drag onto a field (placed ${count} time${
                count > 1 ? 's' : ''
              } — drag again to add another, e.g. one per month)`
            : `${def.label}: drag onto a field`;
        return (
          <span
            key={def.key}
            className={`mapping-tool-qs-badge ${def.cls} ${count > 0 ? 'used' : ''}`}
            draggable
            title={title}
            onDragStart={e => onDragStart(e, def)}
          >
            {def.label}
            {count > 0 && <span className="mapping-tool-chip-count">{`×${count}`}</span>}
          </span>
        );
      })}
    </span>
    <span className="mapping-tool-prompt-text">{prompt.text}</span>
  </li>
);

PromptRow.propTypes = {
  prompt: PropTypes.shape({ id: PropTypes.string.isRequired, text: PropTypes.string.isRequired })
    .isRequired,
  badgeDefs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      cls: PropTypes.string.isRequired
    })
  ).isRequired,
  usedCounts: PropTypes.objectOf(PropTypes.number).isRequired,
  onDragStart: PropTypes.func.isRequired
};

const mappedFieldIdentifier = field =>
  field.source === 'acro' ? field.name : `${Math.round(field.x)}, ${Math.round(field.y)}px`;

const MappedFieldRow = ({ field, onJump, onRemove, onPeriodChange }) => (
  <li className="mapping-tool-mapped-item">
    <button type="button" className="mapping-tool-mapped-jump" onClick={onJump}>
      <span className="mapping-tool-mapped-row-main">
        <span className="mapping-tool-mapped-page">{`Pg ${field.page}`}</span>
        <span className={`mapping-tool-mapped-type ${field.source}`}>
          {field.source === 'acro' ? 'AcroForm' : 'Drawn'}
        </span>
        <span className="mapping-tool-mapped-label">{field.mappedKey.label}</span>
      </span>
      <span className="mapping-tool-mapped-identifier">{mappedFieldIdentifier(field)}</span>
    </button>
    <input
      type="text"
      className="mapping-tool-mapped-period"
      placeholder="period"
      title="Which occurrence is this? e.g. Jan, Feb — leave blank if this key is placed only once"
      value={field.period || ''}
      onClick={e => e.stopPropagation()}
      onChange={e => onPeriodChange(e.target.value)}
    />
    <button
      type="button"
      className="mapping-tool-mapped-remove"
      aria-label="Remove mapping"
      onClick={onRemove}
    >
      ×
    </button>
  </li>
);

MappedFieldRow.propTypes = {
  field: PropTypes.shape({
    page: PropTypes.number.isRequired,
    source: PropTypes.string.isRequired,
    name: PropTypes.string,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    period: PropTypes.string,
    mappedKey: PropTypes.shape({ label: PropTypes.string.isRequired }).isRequired
  }).isRequired,
  onJump: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onPeriodChange: PropTypes.func.isRequired
};

const MappingTool = () => {
  const history = useHistory();
  const [
    {
      file,
      formName,
      formState,
      formYear,
      fields,
      answerFormat,
      passMark,
      failMark,
      naMark,
      inspectionType,
      openPanelId,
      headerSearch,
      promptSearch,
      collapsedCategoryIds,
      mappingPageNum: pageNum,
      headerItems,
      headerItemsLoading,
      headerItemsError,
      passMarkOptions,
      failMarkOptions,
      naMarkOptions,
      inspectionTypeOptions,
      promptSettingsLoading,
      promptSettingsError,
      promptCategories,
      promptCategoriesLoading,
      promptCategoriesError
    },
    {
      setFields,
      setAnswerFormat,
      setPassMark,
      setFailMark,
      setNaMark,
      setInspectionType,
      setOpenPanelId,
      setHeaderSearch,
      setPromptSearch,
      setCollapsedCategoryIds,
      setMappingPageNum: setPageNum,
      fetchGeneralInformation,
      fetchPromptSettings,
      fetchInspectionQuestionSet
    }
  ] = useMappingToolStore();

  const canvasRef = useRef(null);
  const pdfCardRef = useRef(null);

  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const [renderScale, setRenderScale] = useState(1);
  const [dragOverFieldId, setDragOverFieldId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [pendingInspectionType, setPendingInspectionType] = useState(null);

  const goToUpload = () => history.push(UPLOAD_PATH);
  const goToReview = () => history.push(REVIEW_PATH);

  // Prompt mappings are scoped to one question set, so switching type discards them (see
  // setInspectionType). That destroys placed work, so it is confirmed first -- but only when
  // there is work to lose; with nothing mapped the Select stays a plain one-click change.
  const promptMappedCount = fields.filter(f => f.mappedKey?.promptId).length;
  const requestInspectionType = value => {
    if (value === inspectionType) return;
    if (promptMappedCount) {
      setPendingInspectionType(value);
      return;
    }
    setInspectionType(value);
  };
  const confirmInspectionTypeChange = () => {
    setInspectionType(pendingInspectionType);
    setPendingInspectionType(null);
  };
  const togglePanel = id => setOpenPanelId(prev => (prev === id ? null : id));

  useEffect(() => {
    fetchGeneralInformation();
    fetchPromptSettings();
  }, [fetchGeneralInformation, fetchPromptSettings]);

  useEffect(() => {
    fetchInspectionQuestionSet(inspectionType);
  }, [inspectionType, fetchInspectionQuestionSet]);

  useEffect(() => {
    if (!file) return undefined;
    let cancelled = false;
    setLoadError('');
    file
      .arrayBuffer()
      .then(buffer => pdfjsLib.getDocument({ data: buffer }).promise)
      .then(doc => {
        if (cancelled) return;
        setPdfDoc(doc);
        setNumPages(doc.numPages);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not read this PDF.');
      });
    return () => {
      cancelled = true;
    };
  }, [file]);

  useEffect(() => {
    if (!pdfDoc) return undefined;
    let cancelled = false;

    pdfDoc.getPage(pageNum).then(async page => {
      if (cancelled) return;
      const naturalViewport = page.getViewport({ scale: 1 });
      const fitScale = fitScaleFor(naturalViewport.width, pdfCardRef.current);
      const viewport = page.getViewport({ scale: fitScale });
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const context = canvas.getContext('2d');
        if (context) {
          try {
            await page.render({ canvasContext: context, viewport }).promise;
          } catch {
            // best-effort render; environments without a real 2d canvas context land here
          }
        }
      }
      if (cancelled) return;
      setRenderScale(fitScale);
      setPageSize({ width: viewport.width, height: viewport.height });

      const alreadyDetectedThisPage = fields.some(f => f.source === 'acro' && f.page === pageNum);
      if (!alreadyDetectedThisPage) {
        const annotations = await page.getAnnotations();
        const detected = annotations
          .filter(a => a.subtype === 'Widget' && a.fieldType)
          .map(a => {
            const [rx1, ry1, rx2, ry2] = viewport.convertToViewportRectangle(a.rect);
            return {
              id: `acro-${a.id}`,
              name: a.fieldName,
              page: pageNum,
              scale: fitScale,
              x: Math.min(rx1, rx2),
              y: Math.min(ry1, ry2),
              w: Math.abs(rx2 - rx1),
              h: Math.abs(ry2 - ry1),
              source: 'acro',
              period: '',
              mappedKey: null
            };
          });
        if (!cancelled && detected.length) {
          setFields(prev => [...prev, ...detected]);
        }
      }
    });

    return () => {
      cancelled = true;
    };
  }, [pdfDoc, pageNum]);

  const goToPrevPage = () => setPageNum(n => Math.max(1, n - 1));
  const goToNextPage = () => setPageNum(n => Math.min(numPages, n + 1));

  const clearDragOverField = fieldId =>
    setDragOverFieldId(prev => (prev === fieldId ? null : prev));

  const handleKeyDragStart = (e, key, label) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ key, label }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handlePromptDragStart = (e, prompt, categoryId, def) => {
    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        label: def.label,
        promptId: prompt.id,
        outcome: def.key,
        typeId: inspectionType,
        categoryId
      })
    );
    e.dataTransfer.effectAllowed = 'copy';
  };

  const countMappedForOutcome = (promptId, outcomeKey) =>
    fields.filter(
      f =>
        f.mappedKey?.promptId === promptId &&
        f.mappedKey?.outcome === outcomeKey &&
        f.mappedKey?.typeId === inspectionType
    ).length;

  const suggestPeriod = payload =>
    payload.promptId ? String(countMappedForOutcome(payload.promptId, payload.outcome) + 1) : '';

  const handleDropOnField = (e, fieldId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFieldId(null);
    const raw = e.dataTransfer.getData('application/json');
    if (!raw) return;
    const payload = JSON.parse(raw);
    setFields(prev =>
      prev.map(f =>
        f.id === fieldId
          ? { ...f, mappedKey: payload, period: f.period || suggestPeriod(payload) }
          : f
      )
    );
  };

  const handleDropOnStage = e => {
    e.preventDefault();
    setDragOverStage(false);
    const raw = e.dataTransfer.getData('application/json');
    if (!raw) return;
    const payload = JSON.parse(raw);
    const fieldSize = payload.promptId ? NEW_ANSWER_FIELD_SIZE : NEW_FIELD_SIZE;
    const stageRect = e.currentTarget.getBoundingClientRect();
    // Clamped at both ends, matching the move-drag handler. Without the upper bound a badge
    // dropped near the right or bottom edge makes a box that hangs off the page, and its
    // converted PDF point lands outside it -- the value then stamps where nobody can see it.
    const maxX = Math.max(0, stageRect.width - fieldSize.w);
    const maxY = Math.max(0, stageRect.height - fieldSize.h);
    const x = Math.min(Math.max(0, e.clientX - stageRect.left - fieldSize.w / 2), maxX);
    const y = Math.min(Math.max(0, e.clientY - stageRect.top - fieldSize.h / 2), maxY);
    setFields(prev => [
      ...prev,
      {
        id: nextFieldId(),
        page: pageNum,
        scale: renderScale,
        x,
        y,
        w: fieldSize.w,
        h: fieldSize.h,
        source: 'drawn',
        period: suggestPeriod(payload),
        mappedKey: payload
      }
    ]);
  };

  const setFieldPeriod = (fieldId, period) =>
    setFields(prev => prev.map(f => (f.id === fieldId ? { ...f, period } : f)));

  const handleHitboxMouseDown = (e, field) => {
    if (e.button !== 0) return;
    if (e.target.closest('.mapping-tool-hitbox-remove')) return;
    const boxEl = e.currentTarget;
    const startRect = boxEl.getBoundingClientRect();
    const hotzoneX = Math.min(RESIZE_HOTZONE_PX, startRect.width / 3);
    const hotzoneY = Math.min(RESIZE_HOTZONE_PX, startRect.height / 3);
    const inResizeCorner =
      startRect.right - e.clientX < hotzoneX && startRect.bottom - e.clientY < hotzoneY;
    if (inResizeCorner) {
      // The browser owns this drag (.mapping-tool-hitbox is `resize: both`), so the handler
      // still yields -- it just records where the drag ended. Without this the new size lives
      // only in the DOM: the next render reapplies field.w/field.h and the box snaps back,
      // while the saved coordinate and Review's fit-to-box math keep using the old size.
      const commitResize = () => {
        document.removeEventListener('mouseup', commitResize);
        const endRect = boxEl.getBoundingClientRect();
        // Deltas, not absolutes -- field.w/h is the style size, which the measured rect can
        // differ from by the border; the change between the two is the same on either basis.
        const dw = endRect.width - startRect.width;
        const dh = endRect.height - startRect.height;
        if (!dw && !dh) return;
        // Clamped to the stage the same way dragging is, so a resize can't push the box past
        // the page edge and produce a target rect outside the PDF.
        const stage = boxEl.parentElement.getBoundingClientRect();
        setFields(prev =>
          prev.map(f =>
            f.id === field.id
              ? {
                  ...f,
                  w: Math.max(MIN_FIELD_SIZE, Math.min(f.w + dw, stage.width - f.x)),
                  h: Math.max(MIN_FIELD_SIZE, Math.min(f.h + dh, stage.height - f.y))
                }
              : f
          )
        );
      };
      document.addEventListener('mouseup', commitResize);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const stageRect = boxEl.parentElement.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = startRect.left - stageRect.left;
    const startTop = startRect.top - stageRect.top;
    let moved = false;
    boxEl.classList.add('moving');

    const onMouseMove = e2 => {
      const dx = e2.clientX - startX;
      const dy = e2.clientY - startY;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) moved = true;
      const maxLeft = stageRect.width - startRect.width;
      const maxTop = stageRect.height - startRect.height;
      boxEl.style.left = `${Math.max(0, Math.min(startLeft + dx, maxLeft))}px`;
      boxEl.style.top = `${Math.max(0, Math.min(startTop + dy, maxTop))}px`;
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      boxEl.classList.remove('moving');
      if (moved) {
        const newX = parseFloat(boxEl.style.left);
        const newY = parseFloat(boxEl.style.top);
        setFields(prev => prev.map(f => (f.id === field.id ? { ...f, x: newX, y: newY } : f)));
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const removeField = fieldId => {
    setFields(prev => {
      const target = prev.find(f => f.id === fieldId);
      if (!target) return prev;
      if (target.source === 'drawn') {
        return prev.filter(f => f.id !== fieldId);
      }
      return prev.map(f => (f.id === fieldId ? { ...f, mappedKey: null } : f));
    });
  };

  const toggleCategory = id =>
    setCollapsedCategoryIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  const expandAllCategories = () => setCollapsedCategoryIds([]);
  const collapseAllCategories = () => setCollapsedCategoryIds(promptCategories.map(cat => cat.id));

  const filteredHeaderItems = headerItems.filter(item =>
    item.label.toLowerCase().includes(headerSearch.toLowerCase())
  );
  const filteredCategories = promptCategories
    .map(cat => ({
      ...cat,
      prompts: cat.prompts.filter(p => p.text.toLowerCase().includes(promptSearch.toLowerCase()))
    }))
    .filter(cat => cat.prompts.length > 0);

  const promptBadgeDefs = ANSWER_FORMATS[answerFormat];

  const currentPageFields = fields.filter(f => f.page === pageNum);
  const mappedFields = fields.filter(f => f.mappedKey);

  const pdfTitle = [formState, formName, formYear].filter(Boolean).join(' | ') || 'Untitled form';
  const pageInfo = pageSize.width
    ? `Page ${pageNum} of ${numPages} — ${Math.round(pageSize.width / renderScale)}x${Math.round(
        pageSize.height / renderScale
      )}pt`
    : `Page ${pageNum} of ${numPages || 1}`;

  return (
    <Page
      documentName="Inspection Report Template Mapper"
      pageName="Inspection Report Template Mapper - Tool"
      path="/report_template_mapper/tool"
    >
      <div className="mapping-tool-page">
        <div className="mapping-tool-header d-flex align-items-center justify-content-between">
          <div>
            <h1 className="mapping-tool-title">Inspection Report Template Mapper - Tool</h1>
            <p className="mapping-tool-subtitle">
              Mapping your inspection reports never been so easy!
            </p>
          </div>
          <div className="upload-form-header-actions">
            <button type="button" className="btn upload-form-cancel-btn" onClick={goToUpload}>
              Return
            </button>
            <button
              type="button"
              className="btn btn-primary mapping-tool-new-btn"
              onClick={goToReview}
              disabled={!inspectionType}
            >
              Review Mapped Form
            </button>
          </div>
        </div>

        <div className="mapping-tool-list-card">
          <div className="upload-form-stepper">
            {STEPS.map((step, index) => (
              <Fragment key={step.key}>
                <div className="upload-form-step">
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {index < CURRENT_STEP_INDEX ? (
                    <StepComplete className="upload-form-step-icon" />
                  ) : index === CURRENT_STEP_INDEX ? (
                    <RadioChecked className="upload-form-step-icon" />
                  ) : (
                    <RadioUnchecked className="upload-form-step-icon" />
                  )}
                  <span className="upload-form-step-label">{step.label}</span>
                </div>
                {index < STEPS.length - 1 && (
                  <span
                    className={`upload-form-step-line ${
                      index < CURRENT_STEP_INDEX ? 'active' : ''
                    }`}
                  />
                )}
              </Fragment>
            ))}
          </div>
        </div>

        <div className="upload-form-info-banner">
          <InfoIcon className="upload-form-info-icon" />
          <p className="upload-form-info-text mapping-tool-info-single-line">
            Drag options from the right panel and drop them onto the PDF. Place each option exactly
            where you want the final answer to appear.
          </p>
        </div>

        <div className="mapping-tool-workspace">
          <div className="mapping-tool-pdf-card" ref={pdfCardRef}>
            <p className="mapping-tool-pdf-title">{pdfTitle}</p>
            <div className="mapping-tool-pdf-toolbar">
              <button
                type="button"
                className="btn upload-form-cancel-btn"
                disabled={pageNum <= 1}
                onClick={goToPrevPage}
              >
                Prev page
              </button>
              <span className="mapping-tool-pdf-page-info">{pageInfo}</span>
              <button
                type="button"
                className="btn upload-form-cancel-btn"
                disabled={pageNum >= numPages}
                onClick={goToNextPage}
              >
                Next page
              </button>
            </div>

            {!file && (
              <div className="mapping-tool-pdf-stage mapping-tool-pdf-empty">
                <p className="mapping-tool-pdf-placeholder">
                  No PDF loaded — go back to Upload file to select one.
                </p>
              </div>
            )}

            {file && loadError && (
              <div className="mapping-tool-pdf-stage mapping-tool-pdf-empty">
                <p className="mapping-tool-pdf-placeholder">{loadError}</p>
              </div>
            )}

            {file && !loadError && (
              <div
                className={`mapping-tool-stage ${dragOverStage ? 'dragover' : ''}`}
                style={{ width: pageSize.width || undefined, height: pageSize.height || undefined }}
                onDragOver={e => {
                  e.preventDefault();
                  setDragOverStage(true);
                }}
                onDragLeave={() => setDragOverStage(false)}
                onDrop={handleDropOnStage}
              >
                <canvas ref={canvasRef} className="mapping-tool-pdf-canvas" />
                {currentPageFields.map(field => (
                  // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                  <div
                    key={field.id}
                    className={`mapping-tool-hitbox ${field.source} ${
                      field.mappedKey ? 'mapped' : ''
                    } ${dragOverFieldId === field.id ? 'dragover' : ''}`}
                    style={{ left: field.x, top: field.y, width: field.w, height: field.h }}
                    onMouseDown={e => handleHitboxMouseDown(e, field)}
                    onDragOver={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragOverFieldId(field.id);
                    }}
                    onDragLeave={() => clearDragOverField(field.id)}
                    onDrop={e => handleDropOnField(e, field.id)}
                  >
                    {field.mappedKey && (
                      <>
                        <span className="mapping-tool-hitbox-label">
                          {field.mappedKey.label}
                          {field.period ? ` · ${field.period}` : ''}
                        </span>
                        <button
                          type="button"
                          className="mapping-tool-hitbox-remove"
                          aria-label="Remove mapping"
                          onClick={() => removeField(field.id)}
                        >
                          ×
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mapping-tool-sidebar">
            <ProteanCollapsible
              id="header-info"
              className="mapping-tool-side-card"
              open={openPanelId === 'header-info'}
              onToggle={() => togglePanel('header-info')}
              title="Header / General info"
            >
              <p className="mapping-tool-side-instruction">
                Drag a key onto its field. Dashed keys need a join.
              </p>
              <input
                type="text"
                className="form-control mapping-tool-side-search"
                placeholder="Search facility, owner, inspector keys…"
                value={headerSearch}
                onChange={e => setHeaderSearch(e.target.value)}
              />
              {headerItemsLoading && (
                <p className="mapping-tool-side-instruction">Loading header fields…</p>
              )}
              {headerItemsError && (
                <p className="mapping-tool-side-instruction text-danger">{headerItemsError}</p>
              )}
              <ul className="mapping-tool-key-list">
                {filteredHeaderItems.map(item => (
                  <KeyChip
                    key={item.key}
                    label={item.label}
                    onDragStart={e => handleKeyDragStart(e, item.key, item.label)}
                  />
                ))}
              </ul>
            </ProteanCollapsible>

            <ProteanCollapsible
              id="calendar-labels"
              className="mapping-tool-side-card"
              open={openPanelId === 'calendar-labels'}
              onToggle={() => togglePanel('calendar-labels')}
              title="Calendar Labels"
            >
              <p className="mapping-tool-side-instruction">Drag a month or year onto its field.</p>
              <ul className="mapping-tool-key-list">
                {CALENDAR_ITEMS.map(item => (
                  <KeyChip
                    key={item.key}
                    label={item.label}
                    onDragStart={e => handleKeyDragStart(e, item.key, item.label)}
                  />
                ))}
              </ul>
            </ProteanCollapsible>

            <ProteanCollapsible
              id="prompt-settings"
              className="mapping-tool-side-card"
              open={openPanelId === 'prompt-settings'}
              onToggle={() => togglePanel('prompt-settings')}
              title="Prompt Settings"
            >
              <div className="mapping-tool-settings-field">
                <label htmlFor="mapping-tool-answer-format">Choose the answer format:</label>
                <Select
                  inputId="mapping-tool-answer-format"
                  classNamePrefix="upload-form-select"
                  isSearchable={false}
                  options={ANSWER_FORMAT_OPTIONS}
                  value={ANSWER_FORMAT_OPTIONS.find(option => option.value === answerFormat)}
                  onChange={option => setAnswerFormat(option.value)}
                  menuPortalTarget={document.body}
                  styles={SelectStyled}
                />
              </div>

              <div className="mapping-tool-convention-box">
                <p className="mapping-tool-convention-head">
                  Choose what should be the mark for Pass/Fail/NA
                </p>
                <div className="mapping-tool-convention-fields">
                  <div className="mapping-tool-settings-field">
                    <label htmlFor="mapping-tool-pass-mark">Pass writes:</label>
                    <Select
                      inputId="mapping-tool-pass-mark"
                      classNamePrefix="upload-form-select"
                      isSearchable={false}
                      isLoading={promptSettingsLoading}
                      options={passMarkOptions}
                      value={passMarkOptions.find(option => option.value === passMark)}
                      onChange={option => setPassMark(option.value)}
                      menuPortalTarget={document.body}
                      styles={SelectStyled}
                    />
                  </div>
                  <div className="mapping-tool-settings-field">
                    <label htmlFor="mapping-tool-fail-mark">Fail writes:</label>
                    <Select
                      inputId="mapping-tool-fail-mark"
                      classNamePrefix="upload-form-select"
                      isSearchable={false}
                      isLoading={promptSettingsLoading}
                      options={failMarkOptions}
                      value={failMarkOptions.find(option => option.value === failMark)}
                      onChange={option => setFailMark(option.value)}
                      menuPortalTarget={document.body}
                      styles={SelectStyled}
                    />
                  </div>
                  <div className="mapping-tool-settings-field">
                    <label htmlFor="mapping-tool-na-mark">NA writes:</label>
                    <Select
                      inputId="mapping-tool-na-mark"
                      classNamePrefix="upload-form-select"
                      isSearchable={false}
                      isLoading={promptSettingsLoading}
                      options={naMarkOptions}
                      value={naMarkOptions.find(option => option.value === naMark)}
                      onChange={option => setNaMark(option.value)}
                      menuPortalTarget={document.body}
                      styles={SelectStyled}
                    />
                  </div>
                </div>
                {promptSettingsError && (
                  <p className="mapping-tool-side-instruction text-danger">{promptSettingsError}</p>
                )}
              </div>

              <div className="mapping-tool-settings-field">
                <label htmlFor="mapping-tool-inspection-type">Choose the inspection type:</label>
                <Select
                  inputId="mapping-tool-inspection-type"
                  classNamePrefix="upload-form-select"
                  title="Browse by inspection type"
                  isLoading={promptSettingsLoading}
                  options={inspectionTypeOptions}
                  value={inspectionTypeOptions.find(option => option.value === inspectionType)}
                  onChange={option => requestInspectionType(option.value)}
                  menuPortalTarget={document.body}
                  styles={SelectStyled}
                />
              </div>
            </ProteanCollapsible>

            <ProteanCollapsible
              id="prompt-keys"
              className="mapping-tool-side-card"
              open={openPanelId === 'prompt-keys'}
              onToggle={() => togglePanel('prompt-keys')}
              title="Inspection Prompt Keys"
            >
              {!inspectionType && (
                <p className="mapping-tool-side-instruction">
                  Choose an inspection type above to load its prompt catalog.
                </p>
              )}
              {promptCategoriesLoading && (
                <p className="mapping-tool-side-instruction">Loading prompt catalog…</p>
              )}
              {promptCategoriesError && (
                <p className="mapping-tool-side-instruction text-danger">{promptCategoriesError}</p>
              )}
              <input
                type="text"
                className="form-control mapping-tool-side-search"
                placeholder="Search prompts by text…"
                value={promptSearch}
                onChange={e => setPromptSearch(e.target.value)}
              />
              <div className="mapping-tool-cat-bulk-controls">
                <button
                  type="button"
                  className="mapping-tool-link-btn"
                  onClick={expandAllCategories}
                >
                  Expand all
                </button>
                <button
                  type="button"
                  className="mapping-tool-link-btn"
                  onClick={collapseAllCategories}
                >
                  Collapse all
                </button>
              </div>
              {filteredCategories.map(cat => {
                const isCollapsed = collapsedCategoryIds.includes(cat.id);
                return (
                  <div key={cat.id} className="mapping-tool-cat-group">
                    <button
                      type="button"
                      className="mapping-tool-cat-head"
                      onClick={() => toggleCategory(cat.id)}
                    >
                      <span className="mapping-tool-cat-title">
                        {cat.name}
                        <span className="mapping-tool-cat-count">{` (${cat.prompts.length})`}</span>
                      </span>
                      <span className="mapping-tool-cat-toggle">{isCollapsed ? '▸' : '▾'}</span>
                    </button>
                    {!isCollapsed && (
                      <ul className="mapping-tool-key-list mapping-tool-cat-body">
                        {cat.prompts.map(prompt => (
                          <PromptRow
                            key={prompt.id}
                            prompt={prompt}
                            badgeDefs={promptBadgeDefs}
                            usedCounts={Object.fromEntries(
                              promptBadgeDefs.map(def => [
                                def.key,
                                countMappedForOutcome(prompt.id, def.key)
                              ])
                            )}
                            onDragStart={(e, def) => handlePromptDragStart(e, prompt, cat.id, def)}
                          />
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </ProteanCollapsible>

            <ProteanCollapsible
              id="mapped-fields"
              className="mapping-tool-side-card"
              open={openPanelId === 'mapped-fields'}
              onToggle={() => togglePanel('mapped-fields')}
              title={`Mapped Fields (${mappedFields.length})`}
            >
              {mappedFields.length ? (
                <>
                  <p className="mapping-tool-side-instruction">
                    Rename repeated placements to their period (e.g. Jan).
                  </p>
                  <ul className="mapping-tool-mapped-list">
                    {mappedFields.map(field => (
                      <MappedFieldRow
                        key={field.id}
                        field={field}
                        onJump={() => setPageNum(field.page)}
                        onRemove={() => removeField(field.id)}
                        onPeriodChange={period => setFieldPeriod(field.id, period)}
                      />
                    ))}
                  </ul>
                </>
              ) : (
                <p className="mapping-tool-side-instruction">No fields mapped yet.</p>
              )}
            </ProteanCollapsible>
          </div>
        </div>
      </div>

      <Modal
        open={pendingInspectionType !== null}
        onClose={() => setPendingInspectionType(null)}
        center
        focusTrapped={false}
        showCloseIcon={false}
        classNames={{ modal: 'mapping-tool-save-modal' }}
      >
        <div className="mapping-tool-save-modal-header">
          <div>
            <h2 className="mapping-tool-save-modal-title">Change inspection type</h2>
            <p className="mapping-tool-save-modal-subtitle">Confirm Change</p>
            <p className="mapping-tool-save-modal-body">
              {`Prompt mappings belong to the inspection type they were placed under, so changing
              it will remove the ${promptMappedCount} prompt mapping${
                promptMappedCount === 1 ? '' : 's'
              } you have placed. General info, comment, photo and date fields are kept.`}
            </p>
          </div>
          <button
            type="button"
            className="mapping-tool-save-modal-close"
            aria-label="Close"
            onClick={() => setPendingInspectionType(null)}
          >
            ×
          </button>
        </div>
        <div className="mapping-tool-save-modal-buttons">
          <button
            type="button"
            className="mapping-tool-save-modal-return-btn"
            onClick={() => setPendingInspectionType(null)}
          >
            Return
          </button>
          <button
            type="button"
            className="mapping-tool-save-modal-confirm-btn"
            onClick={confirmInspectionTypeChange}
          >
            Change type
          </button>
        </div>
      </Modal>
    </Page>
  );
};

export default MappingTool;
